import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ActivityFeedConversationFilter } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/ActivityView/Header/ActivityFeedFilter/ConversationFilter";

import type { TaskSummary } from "Types/TaskTypes/TaskSummary";

/**
 * Dictionary as a const to be able to use the keys as index, and because they are more
 * friendly than enums.
 *
 * @see https://www.youtube.com/watch?v=0fTdCSH_QEU
 */

export const InputPersistNamespaces = {
    TEXT_MESSAGE_INPUT: "textMessageInput",
    TEXT_MESSAGE_LEXICAL_INPUT: "textMessageLexicalInput",
    EMAIL_INPUT: "emailInput",
    EMAIL_LEXICAL_INPUT: "emailLexicalInput",
    COPILOT_LEXICAL_INPUT: "copilotLexicalInput",
    TASK_SUMMARIES: "taskSummaries",
    ACTIVE_CONTACT_PANEL_TAB: "activeContactPanelTab",
    ACTIVITY_FEED_CONVERSATION_FILTER: "activityFeedConversationFilter",
} as const;

export type InputPersistNamespace = (typeof InputPersistNamespaces)[keyof typeof InputPersistNamespaces];

// A simple map between the namespace and the data type

export type DataByNamespace = {
    [InputPersistNamespaces.TEXT_MESSAGE_INPUT]: string;
    [InputPersistNamespaces.TEXT_MESSAGE_LEXICAL_INPUT]: string;
    [InputPersistNamespaces.EMAIL_INPUT]: string;
    [InputPersistNamespaces.EMAIL_LEXICAL_INPUT]: string;
    [InputPersistNamespaces.COPILOT_LEXICAL_INPUT]: string;
    [InputPersistNamespaces.TASK_SUMMARIES]: TaskSummary;
    [InputPersistNamespaces.ACTIVE_CONTACT_PANEL_TAB]: string;
    [InputPersistNamespaces.ACTIVITY_FEED_CONVERSATION_FILTER]: ActivityFeedConversationFilter;
};

// Generate the state dynamically based on the namespace and the data type

export type InputPersistState = {
    [namespace in InputPersistNamespace]: Record<string, DataByNamespace[namespace]>;
};

// Helper that narrows N to a specific key so TypeScript evaluates
// InputPersistState[N] as a concrete type rather than a union-collapsed intersection.
function initNamespace<N extends InputPersistNamespace>(state: Partial<InputPersistState>, namespace: N) {
    if (!state[namespace]) {
        state[namespace] = {} as InputPersistState[N];
    }
}

const initialState: InputPersistState = {
    textMessageInput: {},
    textMessageLexicalInput: {},
    emailInput: {},
    emailLexicalInput: {},
    copilotLexicalInput: {},
    taskSummaries: {},
    activeContactPanelTab: {},
    activityFeedConversationFilter: {},
};

const inputPersistSlice = createSlice({
    name: "inputPersist",
    initialState,
    reducers: {
        setUserInput: (
            state,
            action: PayloadAction<{
                namespace: InputPersistNamespace;
                key: string;
                value: DataByNamespace[InputPersistNamespace];
            }>
        ) => {
            const { namespace, key, value } = action.payload;
            // Backwards-compatible: older persisted state may not include new namespaces.
            // The type says every namespace is required, but persisted state predating a
            // namespace addition won't have it — so we initialise it defensively here.
            initNamespace(state as Partial<InputPersistState>, namespace);
            (state[namespace] as Record<string, DataByNamespace[InputPersistNamespace]>)[key] = value;
        },
        clearUserInput: (state, action: PayloadAction<{ namespace: InputPersistNamespace; key: string }>) => {
            const { namespace, key } = action.payload;
            if (!(state as Partial<InputPersistState>)[namespace]) {
                return;
            }
            delete (state[namespace] as Record<string, unknown>)[key];
        },
        resetUserInput: (state, action: PayloadAction<{ namespace: InputPersistNamespace }>) => {
            const { namespace } = action.payload;
            (state as Record<InputPersistNamespace, Record<string, unknown>>)[namespace] = {};
        },
    },
});

export const { setUserInput, clearUserInput, resetUserInput } = inputPersistSlice.actions;

export default inputPersistSlice.reducer;
